import React, { useEffect, useRef } from 'react';
import { View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { useThemePalette } from '@/config/theme';

/**
 * Full-banner goal celebration: when the live score climbs while you are
 * watching, a spinning ball and a bold "GOAL" pop into the centre of the match
 * header, hold for a beat, then fade away. Pure overlay (pointerEvents none), so
 * it never blocks taps. It only fires on increases that happen AFTER mount, so
 * opening a match that is already 2-1 stays quiet.
 */
export function GoalCelebration({ total, active }: { total: number; active: boolean }) {
  const palette = useThemePalette();
  const reduce = useReducedMotion();
  const previous = useRef(total);
  const progress = useSharedValue(0);
  const ball = useSharedValue(0);

  useEffect(() => {
    if (active && total > previous.current) {
      if (reduce) {
        progress.value = withSequence(
          withTiming(1, { duration: 180 }),
          withDelay(2400, withTiming(0, { duration: 400 })),
        );
        ball.value = 1;
      } else {
        progress.value = withSequence(
          withTiming(1, { duration: 280, easing: Easing.out(Easing.back(1.7)) }),
          withDelay(2300, withTiming(0, { duration: 460 })),
        );
        ball.value = 0;
        ball.value = withSequence(
          withSpring(1, { damping: 8, stiffness: 110 }),
          withDelay(2300, withTiming(0, { duration: 320 })),
        );
      }
    }
    previous.current = total;
  }, [total, active, reduce, progress, ball]);

  const scrimStyle = useAnimatedStyle(() => ({ opacity: progress.value * 0.82 }));
  const contentStyle = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [{ scale: 0.62 + progress.value * 0.38 }],
  }));
  const ballStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: (1 - ball.value) * 10 },
      { rotate: `${ball.value * 360}deg` },
      { scale: 0.85 + ball.value * 0.15 },
    ],
  }));

  return (
    <View
      pointerEvents="none"
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' }}
    >
      <Animated.View
        style={[
          { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(11,11,26,1)' },
          scrimStyle,
        ]}
      />
      <Animated.View style={[{ alignItems: 'center', gap: 6 }, contentStyle]}>
        <Animated.Text style={[{ fontSize: 46 }, ballStyle]}>⚽</Animated.Text>
        <View
          style={{
            paddingHorizontal: 22,
            paddingVertical: 7,
            borderRadius: 999,
            backgroundColor: palette.volt,
          }}
        >
          <AppText weight="bold" style={{ color: palette.onVolt, fontSize: 26, letterSpacing: 4 }}>
            GOAL
          </AppText>
        </View>
      </Animated.View>
    </View>
  );
}
